import React, { useState, useEffect } from 'react';
import { Cookie, X } from 'lucide-react';

const CookieConsent: React.FC = () => {
  const [showConsent, setShowConsent] = useState<boolean>(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    // Only show consent banner in production or if forced by query param
    const forceConsent = new URLSearchParams(window.location.search).get('showConsent');
    // Show consent if:
    // 1. No consent has been given previously AND
    // 2. Either in production OR forced by query param OR testing cookie banner
    if (!consent && (import.meta.env.PROD || forceConsent === 'true' || forceConsent === 'test')) {
      setShowConsent(true);
    }
  }, []);

  const acceptAll = () => {
    localStorage.setItem('cookie-consent', 'all');
    setShowConsent(false);
    // Initialize analytics in production
    if (import.meta.env.PROD) {
      // Initialize Google Analytics
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'analytics_storage': 'granted',
          'ad_storage': 'granted'
        });
      }
    }
  };

  const acceptEssential = () => {
    localStorage.setItem('cookie-consent', 'essential');
    setShowConsent(false);
    // Initialize limited analytics in production
    if (import.meta.env.PROD) {
      // Initialize Google Analytics with limited consent
      if (window.gtag) {
        window.gtag('consent', 'update', {
          'analytics_storage': 'denied',
          'ad_storage': 'denied'
        });
      }
    }
  };

  if (!showConsent) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-50" role="dialog" aria-labelledby="cookie-consent-title">
      <div className="container mx-auto max-w-4xl">
        <div className="flex items-start gap-4">
          <div className="bg-avn-purple-100 p-2 rounded-lg">
            <Cookie className="h-6 w-6 text-avn-purple-600" />
          </div>
          <div className="flex-1">
            <h3 id="cookie-consent-title" className="text-lg font-semibold mb-2">Cookie Preferences</h3>
            <p className="text-gray-600 mb-4">
              We use cookies to enhance your browsing experience, serve personalized content, and analyze 
              our traffic. By clicking "Accept All", you consent to our use of cookies. You can manage 
              your preferences by clicking "Essential Only".
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={acceptAll}
                className="bg-[#4B0082] text-white px-6 py-2 rounded-lg hover:bg-[#3b0065] transition-colors"
                aria-label="Accept all cookies"
              >
                Accept All
              </button>
              <button
                onClick={acceptEssential}
                className="bg-gray-100 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                aria-label="Accept only essential cookies"
              >
                Essential Only
              </button>
            </div>
          </div>
          <button
            onClick={acceptEssential}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close cookie consent dialog"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;